import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "./useAuth";
import SessionRestoring from "../../components/SessionRestoring";

const STATES = {
  LOADING: "loading",
  ERROR: "error",
  SUCCESS: "success",
};

export default function CleverCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { cleverLogin } = useAuth();
  const [errorMsg, setErrorMsg] = useState("");

  const code = searchParams.get("code");
  const error = searchParams.get("error");

  const isOAuth = Boolean(code || error);

  const [uiState, setUiState] = useState(isOAuth ? STATES.LOADING : null);

  // Prevent double-fire in React Strict Mode
  const didRun = useRef(false);

  useEffect(() => {
    if (!code && !error) return;
    if (didRun.current) {
      return;
    }
    didRun.current = true;

    if (error) {
      setErrorMsg(
        error === "access_denied"
          ? "You canclled the Clever sign-in. Please try again."
          : `Clever returned an error: ${error}`,
      );
      setUiState(STATES.ERROR);
      return;
    }

    if (!code) {
      setErrorMsg("No authorization code recieved from Clever.");
      setUiState(STATES.ERROR);
      return;
    }

    cleverLogin(code)
      .then(() => {
        setUiState(STATES.SUCCESS);
        navigate("/", { replace: true });
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.message ||
          err?.message ||
          "Sign-in failed. Please try again";
        setErrorMsg(msg);
        setUiState(STATES.ERROR);
      });
  }, [isOAuth, code, error, cleverLogin, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOAuth) return null;

  if (uiState === STATES.LOADING) {
    return <SessionRestoring />;
  }

  if (uiState === STATES.SUCCESS) return null;

  if (uiState === STATES.ERROR) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-white px-4">
        <ErrorState errorMsg={errorMsg} />
      </div>
    );
  }

  return null;
}

function ErrorState({ errorMsg }) {
  return (
    <div className="flex max-w-sm flex-col items-center gap-6 rounded-2xl border border-red-100 bg-red-50 p-8 text-center shadow-sm">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
        <svg
          className="h-7 w-7 text-red-500"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth={2}
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z"
          />
        </svg>
      </div>
      <div>
        <h2 className="text-xl font-bold text-gray-900">Sign-in Failed</h2>
        <p className="mt-2 text-sm text-gray-600">{errorMsg}</p>
      </div>
      <a
        href="/"
        className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 active:scale-95"
      >
        ← Back to sign in
      </a>
    </div>
  );
}
